import type { DashboardViewModel } from "../app/dashboardViewModel";
import { isBeforeCalibration } from "../app/calibration";
import { formatGrind } from "../app/grindFormat";
import type { Grinder, Shot } from "../engine/model";
import { TASTE_GLYPH } from "./TasteControl";
import { useLongPress } from "./useLongPress";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const TASTE_WORD = { sour: "sour", balanced: "balanced", bitter: "bitter" } as const;

function when(ts: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(ts);
  if (!m) return ts;
  return `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]} ${m[4]}:${m[5]}`;
}

/** "6 Oct 2026" for the recalibration divider. */
export function calibrationDate(ts: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(ts);
  return m ? `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]} ${m[1]}` : ts;
}

function Row(props: { shot: Shot; grinder: Grinder | null; before: boolean; onMenu: (s: Shot) => void }) {
  const lp = useLongPress(() => props.onMenu(props.shot));
  const s = props.shot;
  const grind = props.grinder ? formatGrind(s.grindSetting, props.grinder) : s.grindSetting.toFixed(1);
  const stars = s.rating === null ? "" : "★".repeat(s.rating);
  const taste = s.taste ? `${TASTE_GLYPH[s.taste]} ${TASTE_WORD[s.taste]}` : "";
  return (
    <li {...lp} class={props.before ? "before-cal" : undefined}>
      <div class="grow">
        <div class="main">Grind {grind} {"·"} {s.timeSeconds.toFixed(1)} s {"·"} {s.yieldG.toFixed(1)} g</div>
        <div class="sub">{[when(s.timestamp), taste, stars, s.notes].filter(Boolean).join("   ")}</div>
      </div>
      <button class="more" type="button" aria-label="Shot actions" onClick={(e) => { e.stopPropagation(); props.onMenu(s); }}>{"⋯"}</button>
    </li>
  );
}

/** Newest first. Shots pulled before the grinder was recalibrated sit under a divider, dimmed: kept, not used. */
export function ShotHistory({ vm, onMenu }: { vm: DashboardViewModel; onMenu: (s: Shot) => void }) {
  const shots = vm.history.value;
  const g = vm.selectedGrinder.value;
  const firstBefore = shots.findIndex((s) => isBeforeCalibration(s, g));
  return (
    <section class="card history" aria-label="Shot history">
      <h2 class="section-title">Shot history</h2>
      <div class="hint">Newest first {"·"} tap {"⋯"} or long-press a row to edit or delete</div>
      {shots.length === 0 ? (
        <div class="empty">No shots yet for this bean on this grinder</div>
      ) : (
        <ul>
          {shots.map((s, i) => [
            i === firstBefore && g?.calibratedSince
              ? <li key="cal-divider" class="cal-divider" role="presentation">Before recalibration on {calibrationDate(g.calibratedSince)} {"·"} not used for predictions</li>
              : null,
            <Row key={s.id ?? s.timestamp} shot={s} grinder={g} before={firstBefore >= 0 && i >= firstBefore} onMenu={onMenu} />,
          ])}
        </ul>
      )}
    </section>
  );
}
